/**
 * Google tag (gtag.js) carregado sob consentimento (Consent Mode v2).
 *
 * O carregamento é disparado por src/lib/consent.ts somente depois que o
 * usuário aceita cookies de análise e/ou marketing. Os defaults "denied" do
 * Consent Mode são declarados inline no index.html, antes de qualquer tag.
 */

import { hasAnalyticsConsent, hasMarketingConsent } from "./consent";

/** Google Analytics 4 (ID publicável, informado pelo cliente). */
export const GA4_ID = "G-2PQDX9L953";


/** Google Ads — conversões. */
export const GOOGLE_ADS_ID = "AW-11380154011";

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/** Empilha um evento no dataLayer (no-op fora do browser). */
export function gtag(...args: unknown[]) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

let loaded = false;

/** Injeta o gtag.js depois do consentimento e da página ficar interativa. */
export function initAnalytics() {
  if (loaded || typeof window === "undefined") return;
  const analytics = hasAnalyticsConsent();
  const marketing = hasMarketingConsent();
  if (!analytics && !marketing) return;
  loaded = true;

  const primaryId = analytics ? GA4_ID : GOOGLE_ADS_ID;

  const load = () => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
    document.head.appendChild(script);

    gtag("js", new Date());
    if (analytics) {
      gtag("config", GA4_ID, { send_page_view: false });
      trackPageView(window.location.pathname + window.location.search);
    }
    if (marketing) {
      gtag("config", GOOGLE_ADS_ID);
    }
  };

  if (document.readyState === "complete") setTimeout(load, 1500);
  else window.addEventListener("load", () => setTimeout(load, 1500), { once: true });
}

/** Envia um page view de SPA (respeita o consentimento de análise). */
export function trackPageView(path: string) {
  if (!hasAnalyticsConsent()) return;
  gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    send_to: GA4_ID,
  });
}

/** Envia um evento customizado ao GA4. */
export function trackAnalyticsEvent(name: string, params: Record<string, unknown> = {}) {
  if (!hasAnalyticsConsent()) return;
  gtag("event", name, { ...params, send_to: GA4_ID });
}
